import React from 'react';
import { EnumType } from 'json-to-graphql-query';
import { IRole, IProfile, IAddress } from '../../../schemas/manage/manage.Schema';
import { ADMIN, BETA, FPC_MANAGE_USER_ROLES_WEB_FALLBACK, GUEST, LEASED_ON_OWNER_OPERATOR_ROLE, PROFILE_FIELDS, PROFILE_FIELDS_PLACEHOLDER, Roles } from '../../../utils/constants';
import { IonCol, IonGrid, IonInput, IonItem, IonLabel, IonRow, IonTextarea } from '@ionic/react';
import DelinkModal from '../../../web/components/common/DelinkPopUp/DelinkModal';
import MobileNumber from '../../../utils/MobileNumber/MobileNumber';
import { normalizeInput } from '../../../pages/Manage/ManageUser/ManageUserUtil';
import { GeographicalService, IONICWrapper } from '../../../utils/common';
import { isElevatedLeasedOwenerOprRole, isElevatedLeasedOwenerOprRoleOnSelectedUser, isLeasedOwnerOprRoleOnly, isLeasedOwnerOprRoleOnlyOnSelectedUser } from '../../../utils/roleUtil/RoleUtil';

/**
 * Get Address List from Azure
 * @param value
 * @param callback
 */
export const fetchAddressList = async (value: any, callback: Function) => {
  if (value.length > 2) {
    await GeographicalService.details.searchAddress(value, (addresses: any) => {
      const list = addresses.map((item: any) => ({
        addressLine1: item.Address.StreetAddress || '',
        addressLine2: '',
        addressType: '',
        city: item.Address.City || '',
        state: item.Address.State || '',
        postalCode: item.Address.Zip || ''
      }));
      callback(list);
    });
  }
};

/**
 * Should Allow Alpha numberic text only
 * @param event
 */
export const handleAlphaNumeric = (value: string): string => {
  return (value && value.replace(/[^a-zA-Z0-9 ]*/g, '')) || '';
};

/**
 * Validate Phone Number
 * @param value
 */
export const handlePhoneNumber = (value: string): boolean => {
  return /^([0-9]{10})$/.test(value);
};

export const isValidString = (value: string): boolean => {
  return !/[~`!#@$%\^&*+=\\[\]\\';,/(){}|\\"\:<>\?]/g.test(value);
};


/**
 * Should allow only Numbers
 * @param event
 */
export const numberOnlyValidation = (value: string): string => {
  return (value && value.replace(/[^+0-9+]*/g, '')) || '';
};

/**
 * Format Phone Numbers
 * @param phone
 */
export const formatPhoneNumber = (phone: string, isCountryCode = false) => {
  const cleaned = `${phone}`.replace(/\D/g, '');
  const match = cleaned.match(/^(1|91|52)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? `+${match[1]}` : '';
    if (isCountryCode) {
      return [intlCode, ' ', match[2], '.', match[3], '.', match[4]].join('');
    }
    return [match[2], '.', match[3], '.', match[4]].join('');
  }
  return null;
};

/**
 * Remove Special Charters
 *
 * @param phone
 */
export const removeSpecial = (phone: string) => {
  let value = phone && phone.replace(/[^+0-9+]*/g, '');
  return value;
};

export const rolesList = [
  { value: 'carrierGuest', name: 'Guest' },
  { value: 'carrierDriver', name: 'Driver' },
  { value: 'carrierDispatcher', name: 'Dispatcher' },
  { value: Roles.carrierLeasedOwnerOpr, name: LEASED_ON_OWNER_OPERATOR_ROLE },
  { value: 'carrierAdmin', name: 'Admin' },
  { value: 'carrierBeta1', name: 'Beta' }
];

export const roles: Array<IRole> = [
  {
    label: BETA,
    value: "carrierBeta1",
    desc: 'Tests and provides feedback for software in pre-launch',
    selected: false,
    displayFlag: false
  },
  {
    label: ADMIN,
    value: Roles.carrierAdmin,
    desc: 'Manages carrier user(s) roles and privileges.',
    selected: false,
    displayFlag: false
  },
  {
    label: GUEST,
    value: Roles.carrierGuest,
    desc: 'Limited access permissions',
    selected: false,
    displayFlag: false
  },
  {
    label: 'Driver',
    value: Roles.carrierDriver,
    desc: 'Carry and update the status of loads.',
    selected: false,
    displayFlag: true
  },
  {
    label: 'Dispatcher',
    value: Roles.carrierDispatcher,
    desc: 'Search, book, carry and update the status of loads.',
    selected: false,
    displayFlag: true
  },
  {
    label: LEASED_ON_OWNER_OPERATOR_ROLE,
    value: Roles.carrierLeasedOwnerOpr,
    desc: 'Search, book, carry and update loads for themselves.',
    selected: false,
    displayFlag: true
  }
];

export const rolesForManageUser : Array<IRole> = [
  {
    label: BETA,
    value: "carrierBeta1",
    desc: 'Tests and provides feedback for software in pre-launch',
    selected: false,
    displayFlag: false
  },
  {
    label: ADMIN,
    value: Roles.carrierAdmin,
    desc: 'Manages carrier user(s) roles and privileges.',
    selected: false,
    displayFlag: false
  },
  {
    label: GUEST,
    value: Roles.carrierGuest,
    desc: 'Limited access permissions',
    selected: false,
    displayFlag: false
  },
  {
    label: 'Driver',
    value: Roles.carrierDriver,
    desc: 'Carry and update the status of loads.',
    selected: false,
    displayFlag: true
  },
  {
    label: 'Dispatcher',
    value: Roles.carrierDispatcher,
    desc: 'Search, book, carry and update the status of loads.',
    selected: false,
    displayFlag: true
  },
  {
    label: LEASED_ON_OWNER_OPERATOR_ROLE,
    value: Roles.carrierLeasedOwnerOpr,
    desc: 'Search, book, carry and update loads for themselves.',
    selected: false,
    displayFlag: true
  }
];

export const carrierField = {
  label: 'Carrier ID',
  name: 'carrierId',
  placeholder: 'Carrier Id',
  className: 'carrierId'
};

export const defaultForm = {
  userId: null,
  roles: '',
  firstName: '',
  lastName: '',
  email: '',
  mobileNumber: '',
  tractorNumber: '',
  addressDTOs: {
    addressLine1: '',
    addressLine2: '',
    addressType: '',
    city: '',
    state: '',
    postalCode: ''
  },
  carrierId: '',
  requestedRoles:'',
  countryCode:"+1"
};

export const formatRoles = (roles: any = [], selectedRoles: any) => {
  const isCarrierAdmin = roles.indexOf('carrierAdmin') !== -1;
  const defaultRoles = roles.map((role: string) => new EnumType(role));
  const finalRoles = getSelectedRoles(selectedRoles);
  return isCarrierAdmin ? [new EnumType('carrierAdmin'), ...finalRoles] : finalRoles.length > 0 ? finalRoles : defaultRoles;
}

/**
 * Get Selecte Roles with enum type
 * @param roles
 */
export const getSelectedRoles = (roles: Array<IRole>) => {
  return roles?.filter(role => role.selected).map(role => new EnumType(role.value));
};

export const  getRequestedRoles = (roles: Array<IRole>) => {
  var requestdRoles = roles?.filter(role => role.selected).map(function(item:any) {
    return item['value'];
  });
  return requestdRoles;
 
};

/**
 * Get Formatted Address
 * @param value
 */
export const getAddressValue = (value: IAddress) => {
  return `${value.city}${value.state && (', ' + value.state)}`.replace(/(^,)|(,$)/g, "");
};

export const ShowPopup = (props: any) => {
  return props.alertProps ? <DelinkModal {...props.alertProps} /> : null
}

export const getFormatedRolesforDrivers = (user: any) => {
  return user && user.userApplicationDetails && user.userApplicationDetails[0].roles.map((role: any) => {
    const selectedOption = rolesList.find((option: any) => option.value === role.roleName);
    return selectedOption ? selectedOption.name : '';
  }).join(', ') || '';
}

export const getFormatedRoles = (user: any) => {
  return user && user.roles && user.roles.map((role: any) => {
    const selectedOption = rolesList.find((option: any) => option.value === role);
    return selectedOption ? selectedOption.name : '';
  }).join(', ') || '';
}

export const getFormatedRolesWithSemiColon = (user: any) => {
  return user && user.roles && user.roles.map((role: any) => {
    const selectedOption = rolesList.find((option: any) => option.value === role);
    return selectedOption ? selectedOption.name : '';
  }).join('; ') || '';
}

export const getUserFields = (selectedUser: any) => {
  const roles = getFormatedRoles(selectedUser) || 'Guest';
  const driverFields = ((selectedUser?.userApplicationDetails &&
    selectedUser?.userApplicationDetails[0]?.roles?.find((item: any) => item.roleName === 'carrierDriver')) || isLeasedOwnerOprRoleOnlyOnSelectedUser(selectedUser?.roles) || isElevatedLeasedOwenerOprRoleOnSelectedUser(selectedUser?.roles))? [
    {
      label: PROFILE_FIELDS.PARK_LOCATION,
      value: selectedUser.address && selectedUser.address.length > 0 ? (getAddressValue(selectedUser.address[0]) || '-') : '-'
    },
    {
      label: PROFILE_FIELDS.TRACKTOR_NUMBER,
      value: selectedUser.tractorNumber || '-'
    },
  ] : [];
  const userProfile: Array<any> = selectedUser ? [
    {
      label: PROFILE_FIELDS.ROLE,
      value: roles
    },
    {
      label: PROFILE_FIELDS.FIRST_NAME,
      value: selectedUser.firstName
    },
    {
      label: PROFILE_FIELDS.LAST_NAME,
      value: selectedUser.lastName
    },
    {
      label: PROFILE_FIELDS.EMAIL,
      value: selectedUser.email
    },
    {
      label: PROFILE_FIELDS.MOBILE_NUMBER,
      value: selectedUser.mobileNumber
    },
    ...driverFields,
    {
      label: PROFILE_FIELDS.CARRIER_ID,
      value: selectedUser.carrierId || 'NA'
    },
  ] : [];
  const defaultRolesForManageUser = JSON.parse(IONICWrapper?.details?.keys?.configuration?.fpc_manage_user_roles_web || FPC_MANAGE_USER_ROLES_WEB_FALLBACK) || rolesForManageUser;
  return userProfile.length > 0
    ? userProfile.map((profile: any) =>
    profile.label === PROFILE_FIELDS.ROLE ?<>
    <IonLabel
    className="user-profile-label"
    position="floating"
    mode="ios"
  >
    {profile.label}
  </IonLabel>
    <IonGrid class="select-role-grid view">
     
<IonGrid class="role-suggetion-grid">
{ profile?.value?.split(",").map((roleSplit: any) => (

defaultRolesForManageUser.map((roles: any, index: number)=>{
return roleSplit.includes(roles.label) &&

<IonRow
key={index}
class="user-profile-input opt-row"
>
<IonCol class="role-column">
  <IonLabel class="role-type">{" "}{(roles.label)}</IonLabel>
  <IonRow class="role-desc">{(roles.desc)}</IonRow>
</IonCol>
</IonRow>

})

))}
</IonGrid>
</IonGrid> </> :
        profile.label !== PROFILE_FIELDS.MOBILE_NUMBER ? (
          <IonItem className="user-profile-item" key={profile.label} text-wrap>
            <IonLabel
              className="user-profile-label"
              position="floating"
              mode="ios"
            >
              {profile.label}
            </IonLabel>
              <IonInput
                value={profile.value}
                readonly
                type="text"
                class="user-profile-input"
              ></IonInput>
          </IonItem>
        ) : (
          <IonItem className="user-profile-item" key={profile.label}>
            <MobileNumber
              mobileNumber={normalizeInput(profile.value)}
              mobileReadOnly={true}
              name="mobileNumber"
              placeholder={""}
              label={profile.label}
              nonEditableField={true}
              countryCodefromMember={removeSpecial(profile.value)?.slice(
                0,
                -10
              )}
            />
          </IonItem>
        )
      )
    : null;
}